import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Component, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import { finalize, first, switchMap, tap } from 'rxjs/operators';
import { Messages, Permission } from 'src/app/common/enums';
import { AgreementTemplateSubjectData } from 'src/app/common/interfaces/agreement-template-subject-data';
import { GenerateEmploymentAgreementRequest } from 'src/app/contracts/requests/generate-employment-agreement-request';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { EmploymentAgreementService } from 'src/app/data/employment-agreement.service';
import { EmploymentConditionsConfirmationService } from 'src/app/data/employment-conditions-confirmation.service';
import { WorkerAgreementService } from 'src/app/data/worker-agreement.service';
import { DictionaryItem } from 'src/app/models/dictionary-item';
import { EmploymentAgreementDto } from 'src/app/models/dtos/employment-agreement-dto';
import { EmploymentType } from 'src/app/models/enums/employment-type-enum';
import { ConfirmDialogComponent } from 'src/app/shared/messages/confirm-dialog/confirm-dialog.component';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-summary-step',
  templateUrl: './summary-step.component.html',
  styleUrls: ['./summary-step.component.scss'],
  standalone: false
})
export class SummaryStepComponent implements OnInit, OnChanges {
  private dictionaryService = inject(DictionaryService);
  private formBuilder = inject(UntypedFormBuilder);
  private spinner = inject(NgxSpinnerService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private employmentAgreementService = inject(EmploymentAgreementService);
  private snackbarService = inject(SnackBarService);
  private employmentConditionsConfirmationService = inject(EmploymentConditionsConfirmationService);
  private authService = inject(AuthService);
  private dialog = inject(MatDialog);
  private translate = inject(TranslateService);
  private workerAgreementService = inject(WorkerAgreementService);

  private workerId: number;
  private employmentTypeId: number;
  private agreementId: number;
  private employerId: number;

  private agreementTemplatesSubject = new BehaviorSubject<AgreementTemplateSubjectData>(null);
  employmentConditionsConfirmationTemplates$: Observable<DictionaryItem[]> = null;

  listOfAgreementTemplates$: Observable<DictionaryItem[]> = this.agreementTemplatesSubject.asObservable().pipe(
    switchMap((data: AgreementTemplateSubjectData) => {
      if (!data?.employmentTypeId) return of([]);
      return this.dictionaryService.getAgreementTemplates(data?.employerId, data.employmentTypeId);
    }),
  );

  @Input() prefetchedAgreement: EmploymentAgreementDto;

  agreementFormGroup: UntypedFormGroup;

  constructor() {
    this.workerId = this.route.snapshot.params.workerId;
    this.employmentTypeId = +this.route.snapshot.params.employmentTypeId;
    this.agreementId = this.route.snapshot.params.agreementId;
    this.agreementTemplatesSubject.next(<AgreementTemplateSubjectData>{ employerId: null, employmentTypeId: this.employmentTypeId });
  }

  get agreementTemplate() {
    return this.agreementFormGroup.get('agreementTemplate') as UntypedFormControl;
  }

  get employmentConditionsConfirmationTemplate() {
    return this.agreementFormGroup?.get('employmentConditionsConfirmationTemplate') as UntypedFormControl;
  }

  ngOnInit(): void {
    this.buildFormGroup();
    this.initAgreementTemplateControl();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.prefetchedAgreement && changes.prefetchedAgreement.currentValue) {
      this.employerId = changes.prefetchedAgreement.currentValue.EmployerId;

      this.agreementTemplatesSubject.next(<AgreementTemplateSubjectData>{
        employerId: changes.prefetchedAgreement.currentValue.EmployerId,
        hasEmploymentConditionsConfirmations: changes.prefetchedAgreement.currentValue.hasEmploymentConditionsConfirmations,
        employmentTypeId: this.employmentTypeId,
      });

      this.updateFormGroupValidators();
    }
  }

  initAgreementTemplateControl(): void {
    this.agreementFormGroup.get('agreementTemplate').valueChanges.subscribe((value) => {
      this.employmentConditionsConfirmationTemplates$ = this.getEmploymentConditionsConfirmationTemplates(value).pipe(
        tap((items: any[]) => {
          if (items.length === 1) {
            this.employmentConditionsConfirmationTemplate.setValue(items[0].Id);
          }
        }),
      );
    });
  }

  isEmploymentConditionsConfirmationTemplateVisible(): boolean {
    return this.prefetchedAgreement?.HasEmploymentConditionsConfirmations &&
      (this.employmentTypeId === EmploymentType.EmploymentAgreement || this.employmentTypeId === EmploymentType.TemporaryEmploymentAgreement);
  }

  getEmploymentConditionsConfirmationTemplates(value: number): Observable<DictionaryItem[]> {
    return this.employmentConditionsConfirmationService.getEmploymentConditionsConfirmationTemplatesByEmployer(this.employerId, value, this.employmentTypeId);
  }

  isDeleteButtonVisible(): boolean {
    return !!this.prefetchedAgreement?.AuthServerUserId && (
      this.authService.hasPermission(Permission.ManageMyExternalWorkersList) ||
      this.authService.hasPermission(Permission.ManageAllExternalWorkersList)
    ) && this.prefetchedAgreement.AuthServerUserId !== this.authService.getAuthServerUserId();
  }

  isDeleteButtonEnabled(): boolean {
    return this.isDeleteButtonVisible() && this.prefetchedAgreement?.WorkerAgreementCountInCompany > 1;
  }

  deleteButtonTooltip(): string {
    if (this.isDeleteButtonVisible() && this.prefetchedAgreement?.WorkerAgreementCountInCompany <= 1) {
      return this.translate.instant('WAD-DeleteAgreementTooltip');
    }
    return '';
  }

  onDeleteAgreementClick(): void {
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: Messages.ConfirmDeletingAgreementTitle,
          message: this.translate.instant('ConfirmDeletingAgreementMessage', { status: this.prefetchedAgreement?.AgreementStatus }),
        },
      })
      .afterClosed()
      .pipe(
        first(),
        switchMap((isConfirmed) => {
          if (!isConfirmed) { return EMPTY; }
          this.spinner.show();
          return this.workerAgreementService.deleteAgreement(this.agreementId).pipe(
            finalize(() => this.spinner.hide()),
            tap(() => {
              this.snackbarService.openSuccessSnackBar(Messages.SuccessfullyDeletedAgreement);
              this.router.navigate(['/profile', this.workerId, 'workerAgreements']);
            }),
          );
        }),
      )
      .subscribe();
  }

  onGenerateDocument(): void {
    if (this.agreementFormGroup.invalid) {
      return;
    }
    this.spinner.show();
    this.employmentAgreementService
      .generateEmploymentAgreement(this.agreementId, <GenerateEmploymentAgreementRequest>{ AgreementTemplateId: this.agreementTemplate.value, EmploymentConditionsConfirmationTemplateId: this.employmentConditionsConfirmationTemplate.value })
      .pipe(
        first(),
        tap(() => {
          this.snackbarService.openSuccessSnackBar(Messages.SuccessfullySendAgreementToGeneration);
          this.router.navigate(['/workers', this.workerId, 'employmentType', this.employmentTypeId, 'agreements', this.agreementId, 'details']);
        }),
        finalize(() => this.spinner.hide()),
      )
      .subscribe();
  }

  private buildFormGroup(): void {
    this.agreementFormGroup = this.formBuilder.group({
      agreementTemplate: [null, [Validators.required]],
      employmentConditionsConfirmationTemplate: [null, this.isEmploymentConditionsConfirmationTemplateVisible() ? [Validators.required] : []],
    });
  }

  private updateFormGroupValidators(): void {
    const employmentConditionsConfirmationControl = this.agreementFormGroup.get('employmentConditionsConfirmationTemplate');
    if (this.isEmploymentConditionsConfirmationTemplateVisible()) {
      employmentConditionsConfirmationControl.setValidators([Validators.required]);
    } else {
      employmentConditionsConfirmationControl.clearValidators();
    }
    employmentConditionsConfirmationControl.updateValueAndValidity();
  }
}
